/**
 * Allowlist HTML sanitiser for the procedure step descriptions shipped as
 * HTML in public/data/timelines (b, br, p, ul, ol, li, u, span with a colour
 * style, a few divs). Token based so it runs in Node for tests; everything
 * not on the allowlist is dropped, script-like elements lose their content,
 * attributes other than a colour style (and a safe href on links) are removed.
 */

const ALLOWED_TAGS = new Set([
  'b', 'strong', 'i', 'em', 'u', 's', 'br', 'p', 'div', 'span', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'sub', 'sup', 'small', 'code', 'pre', 'blockquote',
]);
const VOID_TAGS = new Set(['br', 'hr']);
const DROP_WITH_CONTENT = /<(script|style|iframe|object|embed|svg|math|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const COMMENTS = /<!--[\s\S]*?-->/g;
const TAG = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g;
const ATTR = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
const COLOUR_STYLE = /^\s*color\s*:\s*(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([\d\s,.%]+\))\s*;?\s*$/;
const SAFE_HREF = /^(https?:\/\/|mailto:|\/)/i;

function escapeText(text: string): string {
  return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function attributesFor(tag: string, raw: string): string {
  let out = '';
  for (const m of raw.matchAll(ATTR)) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? '';
    if (name === 'style' && COLOUR_STYLE.test(value)) {
      out += ` style="${value.replace(/"/g, '').trim()}"`;
    } else if (tag === 'a' && name === 'href' && SAFE_HREF.test(value.trim())) {
      out += ` href="${value.trim().replace(/"/g, '%22')}" target="_blank" rel="noopener noreferrer"`;
    }
  }
  return out;
}

/** HTML that only contains the allowlisted tags and attributes. */
export function sanitizeHtml(input: string): string {
  if (!input) return '';
  const source = input.replace(COMMENTS, '').replace(DROP_WITH_CONTENT, '');
  let out = '';
  let last = 0;
  for (const m of source.matchAll(TAG)) {
    const index = m.index ?? 0;
    out += escapeText(source.slice(last, index));
    last = index + m[0].length;
    const tag = m[1].toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) continue;
    const closing = m[0].startsWith('</');
    if (closing) {
      if (!VOID_TAGS.has(tag)) out += `</${tag}>`;
    } else if (VOID_TAGS.has(tag)) {
      out += `<${tag}>`;
    } else {
      out += `<${tag}${attributesFor(tag, m[2])}>`;
    }
  }
  out += escapeText(source.slice(last));
  return out;
}

/** Plain text of an HTML string (tags removed, entities for < > & decoded, whitespace collapsed). */
export function htmlToText(input: string): string {
  return (input || '')
    .replace(COMMENTS, '')
    .replace(DROP_WITH_CONTENT, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|li|div|h[1-6]|tr)>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}
